import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import clsx from 'clsx';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import FormControl from '@material-ui/core/FormControl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Backdrop from '@material-ui/core/Backdrop';
import Chip from '@material-ui/core/Chip';

// import styles-globalStyles
import globalStyles from './../../../styles/globalStyles';
import { chipStyles } from './../../../styles/globalStyles/dataDisplayStyles';

import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ContactsOutlinedIcon from '@material-ui/icons/ContactsOutlined';
import SentimentSatisfiedOutlinedIcon from '@material-ui/icons/SentimentSatisfiedOutlined';
import SentimentDissatisfiedOutlinedIcon from '@material-ui/icons/SentimentDissatisfiedOutlined';

// import react-router
import { Prompt } from 'react-router-dom';

// import Formik and Dependency
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import * as Yup from 'yup';

import GridAvatar from './../../fragments/GridAvatar';
import DropFiles from './../../fragments/DropFiles';

import { logger, openOriginalImage } from './../../../utils/utils';

import { file } from '../../../data';

const useStyles = makeStyles((theme) => ({
  ...globalStyles(theme),
  ...chipStyles(theme),
  card: {
    minWidth: 250,
    maxWidth: 930,
    position: 'relative',
  },
  cardHeader: {
    padding: theme.spacing(2),
    '& > *': {
      margin: theme.spacing(0),
    },
  },
  cardAction: {
    width: '100%',
    justifyContent: 'flex-end',
    '& > *': {
      margin: theme.spacing(1),
    },
  },

  contentWidth: {
    maxWidth: 860,
  },

  formIssueWidth: {
    maxWidth: 890,
  },

  breakWord: {
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    MozWhiteSpace: 'pre-wrap',
  },

  pos: {
    marginBottom: theme.spacing(4),
  },

  // Style thumb-image
  thumbsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16,
  },

  thumb: {
    //display: 'inline-flex',
    borderRadius: 2,
    border: '1px dashed #eaeaea',
    marginBottom: 20,
    marginRight: 8,
    maxWidth: 500,
    // minHeight: 150,
    padding: 4,
    boxSizing: 'border-box',
  },
  thumbInner: {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden',
  },

  img: {
    display: 'block',
    [theme.breakpoints.up('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 500,
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: 500,
    },
    height: '100%',
  },
}));

// YUP: build validation schema (validationSchema)
const validationSchema = Yup.object().shape({
  summary: Yup.string()
    .min(4, 'เรื่องโดยสรุปต้องมากกว่า 4 อักขระ')
    .max(255, 'เรื่องโดยสรุปต้องไม่เกิน 255 อักขระ')
    .required('กรุณากรอกเรื่องโดยสรุป'),
  description: Yup.string()
    .min(4, 'รายละเอียดต้องมากกว่า 4 อักขระ')
    .max(8000, 'รายละเอียดต้องไม่เกิน 8,000 อักขระ')
    .required('กรุณากรอกรายละเอียด'),
  contract: Yup.string()
    .min(4, 'ข้อมูลการติดต่อเพิ่มเติมต้องมากกว่า 4 อักขระ')
    .max(255, 'ข้อมูลการติดต่อเพิ่มเติมต้องไม่เกิน 255 อักขระ')
    .nullable(true),
});

const linkOriginalImage = (issue_id, file_id) => {
  file
    .original({ issue_id, file_id })
    .then((data) => {
      openOriginalImage(data.content);
    })
    .catch((error) => alert('Error openFileOriginal'));
};

/**
 * IssueCard() (Conponent)
 * @param {object} prop
 * @param {string} summary, summary title
 * @param {string} description
 * @param {string} contract
 * @param {number} vote
 * @param {string} createDate
 * @param {string} reporter, issue-reporter name
 * @param {object} avatar, {name, image}
 * @param {string} modifyDate, modify date
 * @param {string} modifier, issue-modifier (modifyBy)
 * @param {array} thumbnails,
 * @param {bool} editing [default: false] Set true if user can edit issue;
 * @param {bool} isClosed [default: false]
 * @param {bool} closing [default: false] Set true if user can close issue;
 * @param {func} onDroppedFiles,
 * @param {func} onRemovedFile,
 * @param {func}  onSubmit,
 * @param {func} onClose, Callback voted (id: GIID (issue id), vote: int) => void
 * @returns IssueCard element
 */

const IssueCard = ({
  id,
  summary,
  description,
  contract,
  vote,
  createDate,
  reporter,
  avatar = {
    image: undefined,
    name: undefined,
  },
  modifyDate = null,
  modifier = null,
  thumbnails = [],
  editing = false,
  isClosed = false,
  closing = false,
  onDroppedFiles,
  onRemovedFile,
  onSubmit,
  onClose,
}) => {
  const classes = useStyles();

  logger('IssueCard render');

  // InitialVaue of form reporting breach
  const initialValues = {
    id,
    summary,
    description,
    contract,
  };

  // Ref, dropFilesRef
  const dropFilesRef = useRef();

  // State isEdit, thumbnails
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const showFormIssue = ({ values, resetForm, touched, errors }) => {
    return (
      <GridAvatar image={avatar.image} name={avatar.name}>
        <Form autoComplete="off">
          <Card className={classes.card} square={false} elevation={0}>
            <CardHeader
              className={classes.cardHeader}
              action={
                <React.Fragment>
                  {closing && !isClosed && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={onClose}
                    >
                      ปิด
                    </Button>
                  )}
                </React.Fragment>
              }
              title={
                <Box
                  display="flex"
                  flexWrap="wrap"
                  bgcolor="background.paper"
                  css={{ maxWidth: '100%' }}
                  alignItems="center"
                >
                  <Box pr={1}>
                    <Typography variant="body2" color="textPrimary">
                      {reporter}
                    </Typography>
                  </Box>
                  <Box pr={1}>
                    <Typography variant="body2" color="textSecondary">
                      เมื่อ: {createDate}
                    </Typography>
                  </Box>
                  <Box>
                    {isClosed ? (
                      <Chip
                        className={classes.chipLabelLargeStyle}
                        label="Close"
                        style={{ color: 'white', backgroundColor: 'red' }}
                      />
                    ) : (
                      <Chip
                        className={classes.chipLabelLargeStyle}
                        label="Open"
                        style={{ color: 'white', backgroundColor: 'green' }}
                      />
                    )}
                  </Box>
                </Box>
              }
              subheader=""
            />
            <Divider />
            <CardContent>
              {!isEdit && (
                <div>
                  {/* Box : issue summary, icon addition, icon edit */}
                  <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="wrap"
                    css={{ maxWidth: '100%' }}
                  >
                    <Box flexGrow={1} mt={2}>
                      <Typography
                        className={classes.pos}
                        variant="h6"
                        component="h2"
                      >
                        <Box>{summary}</Box>
                      </Typography>
                    </Box>
                    <Box mt={2} mr={2}>
                      <Tooltip title="ข้อมูลเพิ่มเติม">
                        <IconButton
                          aria-label="edit issue"
                          size="small"
                          onClick={() => {
                            let x = document.getElementById('more');
                            if (x.style.display === 'none') {
                              x.style.display = 'block';
                            } else {
                              x.style.display = 'none';
                            }
                          }}
                        >
                          <ContactsOutlinedIcon style={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {editing && (
                      <Box mt={2}>
                        <Tooltip title="แก้ไข">
                          <IconButton
                            aria-label="edit issue"
                            size="small"
                            onClick={() => {
                              setIsEdit(true);
                            }}
                          >
                            <EditOutlinedIcon style={{ fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  {/* Typography : issue description */}
                  <Typography
                    variant="body1"
                    component="div"
                    className={classes.pos}
                  >
                    <div
                      className={clsx(classes.breakWord, classes.contentWidth)}
                    >
                      {description}
                    </div>
                  </Typography>

                  {/* Typography : issue addition info */}
                  <Typography
                    id="more"
                    variant="body1"
                    component="div"
                    className={classes.pos}
                    style={{ display: 'none' }}
                  >
                    <Divider />
                    <Box mt={3} fontSize={16} fontWeight="fontWeightBold">
                      ข้อมูลเพิ่มเติม
                    </Box>
                    {contract && (
                      <React.Fragment>
                        <Box my={2} fontWeight="fontWeightBold">
                          ข้อมูลการติดต่อเพิ่มเติม
                        </Box>
                        <Box my={1}>
                          <div
                            className={clsx(
                              classes.breakWord,
                              classes.contentWidth
                            )}
                          >
                            {contract}
                          </div>
                        </Box>
                      </React.Fragment>
                    )}

                    {vote != null && (
                      <React.Fragment>
                        <Box my={2} fontWeight="fontWeightBold">
                          ข้อมูลการโหวด
                        </Box>
                        <Box my={1}>
                          {vote === 1 && (
                            <React.Fragment>
                              <SentimentSatisfiedOutlinedIcon
                                style={{ verticalAlign: 'middle' }}
                              />{' '}
                              พึงพอใจ
                            </React.Fragment>
                          )}
                          {vote === 0 && (
                            <React.Fragment>
                              <SentimentDissatisfiedOutlinedIcon
                                style={{ verticalAlign: 'middle' }}
                              />{' '}
                              ไม่พึงพอใจ
                            </React.Fragment>
                          )}
                        </Box>
                      </React.Fragment>
                    )}
                  </Typography>

                  {/* Apply thumbs style from Dropzone preview component */}
                  <aside className={classes.thumbsContainer}>
                    {thumbnails.map((thumbnail, index) => (
                      <div className={classes.thumb} key={index}>
                        <div className={classes.thumbInner}>
                          <img
                            src={thumbnail.thumbnail}
                            className={classes.img}
                            onClick={() =>
                              linkOriginalImage(
                                thumbnail.issue_id,
                                thumbnail.id
                              )
                            }
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      </div>
                    ))}
                  </aside>
                </div>
              )}
              {isEdit && (
                <div
                  className={clsx(classes.formStyle, classes.formIssueWidth)}
                >
                  <FormControl fullWidth error={false}>
                    <Field
                      component={TextField}
                      name="summary"
                      type="text"
                      label={'เรื่องโดยสรุป *'}
                      size="small"
                      variant="outlined"
                      error={errors.summary && touched.summary ? true : false}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <Field
                      component={TextField}
                      name="description"
                      multiline
                      rowsMax={6}
                      rows={6}
                      label="รายละเอียด *"
                      size="small"
                      variant="outlined"
                      error={
                        errors.description && touched.description ? true : false
                      }
                    />
                  </FormControl>
                  <FormControl fullWidth error={false}>
                    <Field
                      component={TextField}
                      name="contract"
                      multiline
                      rowsMax={6}
                      rows={3}
                      label={'ข้อมูลการติดต่อเพิ่มเติม'}
                      size="small"
                      variant="outlined"
                    />
                  </FormControl>
                  {onDroppedFiles && (
                    <DropFiles
                      ref={dropFilesRef}
                      existFiles={thumbnails}
                      onDropped={onDroppedFiles}
                      onRemoved={onRemovedFile}
                    />
                  )}
                </div>
              )}
            </CardContent>
            {isEdit && (
              <Divider variant="middle" className={classes.formIssueWidth} />
            )}
            <CardActions
              className={clsx(classes.cardAction, classes.formIssueWidth)}
            >
              {!isEdit && modifyDate && modifier && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ width: '100%' }}
                >
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    bgcolor="background.paper"
                  >
                    <Box pt={0.5} pr={1}>
                      แก้ไขล่าสุด เมื่อ: {modifyDate}
                    </Box>
                    <Box pt={0.5} pr={1}>
                      โดย: {modifier}
                    </Box>
                  </Box>
                </Typography>
              )}
              {isEdit && (
                <React.Fragment>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isLoading}
                  >
                    บันทึก
                  </Button>
                  <Button
                    variant="contained"
                    color="default"
                    onClick={(e) => {
                      e.preventDefault();
                      resetForm();
                      setIsEdit(false);
                    }}
                    disabled={isLoading}
                  >
                    ยกเลิก
                  </Button>
                </React.Fragment>
              )}
            </CardActions>
            <Backdrop className={classes.innerBackdropStyle} open={isLoading}>
              <CircularProgress className={classes.circularProgressLoadStyle} />
            </Backdrop>
          </Card>
        </Form>
        <Prompt
          when={JSON.stringify(initialValues) !== JSON.stringify(values)}
          message="การเปลี่ยนแปลงข้อมูลจะไม่ถูกบันทึก"
        />
      </GridAvatar>
    );
  };
  return (
    <div>
      <Formik
        validationSchema={validationSchema}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);

          // dropFilesRef (ref of DropFiles) has new files and also existFiles is included
          // Note: existFiles in dropFilesRef is data (existFiles) assigned to existFiles (become DropFiles's prop), look DropFiles statement

          let issueFiles = [
            ...dropFilesRef.current.files.map((file) => file.id),
          ].join(',');

          let reqData = {
            id: values.id,
            summary: values.summary,
            description: values.description,
            contract: values.contract,
            issueFiles,
          };
          // onSubmit
          (async () => {
            if (onSubmit) {
              !isLoading && setIsLoading(true);
              // values are {issue, comment}
              await onSubmit(reqData);
              setIsLoading(false);
              setIsEdit(false);
            }
          })();
        }}
      >
        {(props) => showFormIssue(props)}
      </Formik>
    </div>
  );
};

/**
 * IssueCard's propTypes
 */
IssueCard.propTypes = {
  reporter: PropTypes.string,
  createDate: PropTypes.string,
  summary: PropTypes.string,
  description: PropTypes.string,
  contract: PropTypes.string,
  avatar: PropTypes.object,
  thumbnails: PropTypes.array,
  isClosed: PropTypes.bool,
  closing: PropTypes.bool,
  editing: PropTypes.bool,
  onDroppedFiles: PropTypes.func,
  onRemovedFile: PropTypes.func,
  onSubmit: PropTypes.func,
};

/**
 * By default it will only shallowly compare complex objects in the props object.
 * If you want control over the comparison,
 * you can also provide a custom comparison function as the second argument.
 * https://reactjs.org/docs/react-api.html
 * https://stackoverflow.com/questions/63502588/avoid-rerendering-child-when-passing-object-in-props
 */
export default React.memo(IssueCard, isEqual);
